import { observable, action, computed } from "mobx"
import request from "app/vendor/request"
import config from "app/config"
import { getRandomColor, splitStrNumber } from "app/vendor/util"

const SINGLE_RELATION_NAME = ["男方", "男方父亲", "男方母亲", "女方", "女方母亲", "女方父亲"]
export default class {
  @observable familyId = "" // 直接写 id 可能会好些
  @observable tableData = []
  @observable historyTableData = []
  @observable selectedRowKeys = []
  @observable totalPrimers = []
  @observable relations = []
  @observable familyInfo = {}
  @observable appInfo = null
  @observable editHistoryList = []
  @observable edited = false
  singleRelations = []

  @action loadFamilyInfo = () => {
    return request.get(`${config.backend}/pgd/family/${this.familyId}`)
    .checkToken()
    .commonDispose()
    .then(
      res => {
        this.familyInfo = res
      }
    )
  }

  @action setFamilyId = (id) => {
    this.familyId = id
  }

  @action initTableData = () => {
    return request.get(`${config.backend}/pgd/family/${this.familyId}/datas`)
    .checkToken()
    .commonDispose()
    .then(result => {
      result.forEach(this.processData)
      this.tableData = result
    })
  }

  processData (sample) {
    sample.hasName = sample.sampleName
    sample.sampleName = sample.sampleName // 手动生成字段
    sample.relationName = sample.relationName
    sample.pathogen = sample.pathogen
    sample.remark = sample.remark
    sample.hasRemark = sample.remark
    sample.marked = false
  }

  @action loadPrimers = () => {
    return request.get(`${config.backend}/pgd/primers`)
    .checkToken()
    .commonDispose()
    .then(res => {
      this.totalPrimers = res
    })
  }
  @action loadRelations = () => {
    return request.get(`${config.backend}/pgd/relations`)
    .checkToken()
    .commonDispose()
    .then(result => {
      this.setRelationColor(result)
      this.relations = result
      this.singleRelations = result.filter(relation => SINGLE_RELATION_NAME.indexOf(relation.name) > -1)
      this.completeRelations()  // 加颜色
      this.updateRelationStatus()
    })
  }

  @action setRelationColor = (arr) => {
    arr.forEach(relation => relation.color = getRandomColor())
  }

  @action onSampleNameChange = (value, sample) => {
    sample.sampleName = value
    this.edited = true
  }

  @action onInputChange = (value, sample, field) => {
    sample[field] = value
    this.edited = true
  }

  /* 因为 select 组件中初始值为数组时必须为 group of ids，
   * 而每一个 primer 又没有唯一的 id，因此选择直接拿它本身做显示值
   * 和 key
   */
  @action onPrimerSelect = (primer, sample) => {
    sample.primers.push(primer)
    this.tableData.forEach(item => item.primers = sample.primers)
    this.edited = true
  }

  @action onPrimerDeselect = (primer, sample) => {
    const primerIndex = sample.primers.findIndex(item => item === primer)
    sample.primers.splice(primerIndex, 1)
    this.tableData.forEach(item => item.primers = sample.primers)
    this.edited = true
  }

  @action onRelationChange = (relationId, sample) => {
    const relation = this.relations.find(relation => relation.id === relationId)
    sample.relationId = relation.id
    sample.relationName = relation.name
    sample.color = relation.color

    this.disposePathogen(sample, relation)

    if (!this.singleRelations.find(relation => relation.id === relationId)) {
      this.addNewRelation(relation)
    }

    this.updateRelationStatus()
    this.edited = true
  }

  @action disposePathogen = (sample) => {
    let pathogen
    const sameRelationList = this.tableData.filter((item) => {
      // 关系相同，且不是同一条数据的。 tip:如果是同一条数据，会出现pathogen字段不变的bug
      return item.relationId === sample.relationId && item.id !== sample.id
    })

    sameRelationList.some((item) => {
      if (item.pathogen) { //
        pathogen = item.pathogen
        return true
      }
      return false
    })

    if (pathogen) {
      sample.pathogen = pathogen
      return
    }

    sample.pathogen = (/^(子代)/).test(sample.relationName) ? "p0,m0" : ""
  }

  @action onPathogenChange = (key, sample) => {
    this.tableData.forEach((item) => {
      if (item.relationId === sample.relationId) {
        item.pathogen = key
      }
    })

    this.edited = true
  }

  /**
  * 遍历列表数据选中的relationId，如果出现次数多余两次，增加marked字段为true。
  * 视图里通过判断marked字段，渲染颜色小球
  */
  @action updateRelationStatus () {
    const tempMap = {}
    this.tableData.forEach(sample => {
      tempMap[sample.relationId] = tempMap[sample.relationId] ? tempMap[sample.relationId] + 1 : 1
    })
    this.tableData.forEach(sample => {
      sample.marked = tempMap[sample.relationId] > 1
    })
  }

  @action updateTableData = () => {
    this.tableData.forEach(sample => {
      sample.hasName = sample.sampleName
      sample.hasRemark = sample.remark
    })
  }

  /**
  * 没选关系不处理
  * 选了关系
  * 1. 选了关系 是基本关系里的 就给他加关系的颜色
  * 2. 选了关系 不是基本关系里的 给他加新颜色
  *   1. 如果
  */
  @action completeRelations = () => {
    this.tableData.forEach(sample => {
      const relation = this.relations.find(relation => relation.id === sample.relationId)
      if (relation) { // 选了关系 是基本关系里的 就给他加关系的颜色
        sample.color = relation.color
      } else {
        if (sample.relationId) { // 选了关系 不是基本关系里的 给他加新颜色
          const newColor = getRandomColor()
          sample.color = newColor
          const position = this.getCompletePosition(sample.relationName)
          const newRelation = {
            id: sample.relationId,
            name: sample.relationName,
            color: newColor
          }
          this.relations.splice(position, 0, newRelation)

          const bundle = splitStrNumber(newRelation.name)
          const newName = bundle.string + (bundle.number + 1)
          if (!this.tableData.find(sample => sample.relationName === newName)) {
            this.addNewRelation(newRelation) // newRelation 被插入后，会被 observable 化，此时 newRelation 不再持有数组堆内存
          }
        }
      }
    })
  }

  /*
   * 我们需要根据一定规律插入到对应的位置
   * 只需要正向遍历即可
   */
  getCompletePosition = (relationName) => {
    const bundle = splitStrNumber(relationName)

    for (let i = this.relations.length - 1; i >= 0; i--) {
      const currentBundle = splitStrNumber(this.relations[i].name)
      if (bundle.string === currentBundle.string && bundle.number > currentBundle.number) {
        return i + 1
      }
    }
  }

  @action addNewRelation = (relation) => {
    const aimIndex = this.relations.findIndex(item => item.name === relation.name) + 1

    const bundle = splitStrNumber(relation.name)
    const newName = bundle.string + (bundle.number + 1)

    if (!this.relations.find(relation => relation.name === newName)) {
      this.relations.splice(aimIndex, 0, {
        gender: relation.gender,
        id: relation.id + aimIndex,
        name: newName,
        color: getRandomColor()
      })
    }
  }

  @action getHistoryTableData = () => {
    return request.get(`${config.backend}/pgd/family/${this.familyId}/tasks`)
    .checkToken()
    .commonDispose()
    .then(
      res => {
        this.historyTableData = res
      }
    )
  }

  @action getTaskDatas = (taskId) => {
    return request.get(`${config.backend}/pgd/family/${this.familyId}/tasks/${taskId}/datas`)
    .checkToken()
    .commonDispose()
  }

  @action setTaskDatas = (id ,data) => {
    const rowIndexInCard = this.historyTableData.findIndex(item => item.id === id)
    this.historyTableData[rowIndexInCard].datas = data
  }

  @action clearTaskDetail = () => {
    this.taskDetail = []
  }

  /*
   * 按照 relationId 对 tableData 进行分组
   */
  @computed get composedData () {
    this.tableData.forEach(data => data.relationName) // 为了使 computed 响应 relationName 的变化, relationId 为什么不行？

    const result = {}
    this.selectedData.forEach(sample => {
      if (result[sample.relationId]) {
        result[sample.relationId].push(sample)
      } else {
        result[sample.relationId] = [sample]
      }
    })

    return result
  }

  @computed get selectedData () {
    return this.tableData.filter(item => this.selectedRowKeys.includes(item.id))
  }

  @computed get runReady () {
    return (this.selectedRowKeys.length > 0 &&
      !this.selectedData.some(sample => !sample.primers.length || !sample.relationName || !sample.pathogen)
    )
  }

  @computed get saveReady () {
    return this.edited
  }

  @action deleteData = (sampleId) => {
    const index = this.tableData.findIndex(data => data.id === sampleId)
    this.tableData.splice(index, 1)
  }

  @action getAppInfo = () => {
    return request.get(`${config.backend}/pgd/apps/${this.familyInfo.appId}`)
    .checkToken()
    .commonDispose()
    .then(res => this.appInfo = res)
  }

  @action clearAppInfo = () => {
    this.appInfo = null
  }

  @action saveDatas = () => {
    return request.put(`${config.backend}/pgd/family/${this.familyId}/datas`)
    .send(this.tableData.slice())
    .checkToken()
    .commonDispose()
  }

  @action deleteSample = (sampleId) => {
    return request.delete(`${config.backend}/pgd/family/${this.familyId}/datas/${sampleId}`)
    .checkToken()
    .commonDispose()
  }

  @action updateSample = (sampleName, remark, sampleId) => {
    return request.put(`${config.backend}/pgd/datas/${sampleId}`)
    .send({sampleName, remark})
    .checkToken()
    .commonDispose()
  }

  @action loadEditHistory = (dataId) => {
    return request.get(`${config.backend}/pgd/datas/${dataId}/history`)
    .checkToken()
    .commonDispose()
    .then(res => this.editHistoryList = res)
  }

  @action clearEditHistory = () => {
    this.editHistoryList = []
  }

  @action runTask = () => {
    return request.post(`${config.backend}/pgd/family/${this.familyId}/tasks?appId=${this.familyInfo.appId}`)
    .send(this.selectedRowKeys.slice())
    .checkToken()
    .commonDispose()
  }

  @action clearState = () => {

  }

  @action updateRemarks = (record, remark) => {
    return request.put(`${config.backend}/pgd/family/${this.familyId}/tasks/${record.id}`)
    .send({remark})
    .checkToken()
    .commonDispose()
  }

  /**
   * 由后端提供支持
   */
  @action parseCSV = (csvText) => {
    return request.put(`${config.backend}/pgd/family/${this.familyId}/datas/csv`)
      .checkToken()
      .send({context: csvText})
      .commonDispose()
      .then(result => {
        result.forEach(this.processData)
        this.tableData = result
        this.completeRelations()  // 加颜色
        this.updateRelationStatus()
      })
  }
}



// WEBPACK FOOTER //
// ./src/app/stores/pgd/dataManageDetail.js